import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@lendcord/concord";
import { IntlProviderWrapper } from "./app/IntlProviderWrapper";
import {
  ForcedPasswordChangeGate,
  RedirectIfAuthenticated,
  RequireAuth,
  RequireRole,
  SessionProvider,
  useSession,
} from "./app/session";
import { ToastProvider } from "./app/toast";
import { getDefaultPathForRole } from "./app/navigation";
import { getRoleFromToken } from "./app/tokenStorage";
import { DashboardLayout } from "./shell/DashboardLayout";
import { AuthPage } from "./auth";
import { DashboardPage } from "./features/dashboard";
import { CreditorPage } from "./features/creditor";
import { ProfilePage } from "./features/profile";
import { ReportsPage } from "./features/reports";
import { OnboardingPage } from "./features/onboarding";
import { MembersPage } from "./features/members";
import { SetupPage } from "./features/setup";
import { JoinRequestsPage } from "./features/join-requests";
import { PeoplePage } from "./features/people";

// "/" has no page of its own — it only ever routes onward based on session
// state, straight to the logged-in user's own first nav item (role-aware —
// see getDefaultPathForRole) or the login screen otherwise.
function RootRedirect() {
  const { isAuthenticated } = useSession();
  return <Navigate to={isAuthenticated ? getDefaultPathForRole(getRoleFromToken()) : "/auth"} replace />;
}

export default function App() {
  return (
    <IntlProviderWrapper>
      <ThemeProvider>
        <ToastProvider>
          <SessionProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<RootRedirect />} />
              <Route element={<RedirectIfAuthenticated />}>
                <Route path="/auth" element={<AuthPage />} />
              </Route>
              <Route element={<RequireAuth />}>
                <Route element={<ForcedPasswordChangeGate />}>
                  <Route element={<RequireRole navKey="dashboard" />}>
                    <Route
                      path="/dashboard"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingDashboard">
                          <DashboardPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="reports" />}>
                    <Route
                      path="/reports"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingReports">
                          <ReportsPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="profile" />}>
                    <Route
                      path="/profile"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingProfile">
                          <ProfilePage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="creditor" />}>
                    <Route
                      path="/creditor"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingCreditor">
                          <CreditorPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="onboarding" />}>
                    <Route
                      path="/onboarding"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingOnboarding">
                          <OnboardingPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="members" />}>
                    <Route
                      path="/members"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingMembers">
                          <MembersPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="setup" />}>
                    <Route
                      path="/setup"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingSetup">
                          <SetupPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="joinRequests" />}>
                    <Route
                      path="/join-requests"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingJoinRequests">
                          <JoinRequestsPage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route element={<RequireRole navKey="people" />}>
                    <Route
                      path="/people"
                      element={
                        <DashboardLayout titleId="lender.topBar.headingPeople">
                          <PeoplePage />
                        </DashboardLayout>
                      }
                    />
                  </Route>
                </Route>
              </Route>
            </Routes>
          </BrowserRouter>
          </SessionProvider>
        </ToastProvider>
      </ThemeProvider>
    </IntlProviderWrapper>
  );
}
